import { Loader2, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getPosClient } from "../lib/api";
import { clearSession, readAccessToken } from "../lib/auth";
import { useFocusTrap } from "../lib/use-focus-trap";

/** Clear any in-progress sale wizard drafts so a corrupted draft
 *  (e.g. PIN digits that bled into the reference field before the
 *  focus trap was in place) doesn't persist after unlock. */
function clearSaleDrafts(): void {
  const prefix = "tilt-pos.wizardDraft.";
  const keys = Object.keys(window.sessionStorage).filter((k) => k.startsWith(prefix));
  keys.forEach((k) => window.sessionStorage.removeItem(k));
}

interface LockScreenProps {
  readonly hasPin: boolean;
  readonly onUnlock: () => void;
}

/**
 * Full-screen lock overlay rendered by AppShell after IDLE_TIMEOUT_MS
 * (15 minutes) of inactivity. Satisfies PCI DSS 4.0 Req 8.2.8.
 *
 * - If the access token has expired: shows the session-expired message
 *   and sends the user to /login immediately.
 * - If the user has a PIN: shows a PIN entry form. Calls
 *   POST /pos/v1/profile/me/verify-pin; 10 failures → 423 lockout.
 * - If the user has no PIN: shows a "sign in again" button only.
 *
 * PIN lockout (PCI DSS 4.0 Req 8.3.4): 423 responses carry
 * ``locked_until``. The screen shows the time and disables the form
 * until that timestamp passes.
 */
export function LockScreen({ hasPin, onUnlock }: LockScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Trap keyboard focus inside the dialog so PIN keystrokes can't reach
  // background form fields (e.g. the sale reference input).
  const trapRef = useFocusTrap<HTMLDivElement>(true, undefined, { escClosesDialog: false });

  // On mount: if the access token expired while the screen was locked,
  // force a full sign-out instead of accepting a PIN for a dead session.
  useEffect(() => {
    const token = readAccessToken();
    console.debug(
      `[lock-screen] mounted — token ${token ? "valid" : "MISSING/EXPIRED"} — hasPin=${String(hasPin)}`,
    );
    if (token === null) {
      console.debug("[lock-screen] token gone — redirecting to /login");
      clearSession();
      navigate("/login", { replace: true });
    } else {
      inputRef.current?.focus();
    }
  }, [navigate, hasPin]);

  // Poll every 5 s while locked so the "try again at X" message
  // disappears automatically when the lockout window expires.
  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      if (new Date() >= lockedUntil) {
        setLockedUntil(null);
        setError(null);
      }
    }, 5_000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  function handleSignInAgain() {
    clearSession();
    navigate("/login", { replace: true });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !pin) return;
    setError(null);
    setSubmitting(true);
    try {
      // Retry up to 2 times on 5xx (Aurora cold-start after scale-to-zero
      // returns 500/503 on the first request while the cluster resumes).
      // Never retry 4xx — those are definitive (wrong PIN, locked out).
      let lastErr: unknown;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await getPosClient().verifyPin({ pin });
          clearSaleDrafts();
          onUnlock();
          return;
        } catch (e: unknown) {
          const status =
            e && typeof e === "object" && "status" in e ? (e as { status: number }).status : 0;
          if (status >= 400 && status < 500) throw e;
          lastErr = e;
          if (attempt < 2) await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
        }
      }
      throw lastErr;
    } catch (err: unknown) {
      if (err && typeof err === "object" && "status" in err) {
        const status = (err as { status: number }).status;
        if (status === 423) {
          const body = (err as { body?: { locked_until?: string } }).body;
          const until = body?.locked_until ? new Date(body.locked_until) : null;
          setLockedUntil(until);
          setError(
            until
              ? t("lock.lockedOut", { time: until.toLocaleTimeString() })
              : t("lock.lockedOut", { time: "?" }),
          );
          setPin("");
        } else if (status === 403) {
          const body = (err as { body?: { attempts_remaining?: number } }).body;
          const remaining = body?.attempts_remaining ?? null;
          setError(
            remaining !== null
              ? t("lock.wrongPin", { count: remaining })
              : t("lock.wrongPin", { count: 0 }),
          );
          setPin("");
          inputRef.current?.focus();
        } else if (status === 404) {
          // No PIN set - shouldn't reach here since hasPin would be false.
          handleSignInAgain();
        } else {
          const msg =
            err && typeof err === "object" && "message" in err
              ? String((err as { message: unknown }).message)
              : t("profile.pin.errors.unknown");
          setError(msg);
        }
      } else {
        setError(err instanceof Error ? err.message : t("profile.pin.errors.unknown"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isLockedOut = lockedUntil !== null && new Date() < lockedUntil;

  return (
    <div
      ref={trapRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t("lock.title")}
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-elev-3">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" aria-hidden="true" />
          </span>
          <h1 className="text-xl font-bold text-strong">{t("lock.title")}</h1>
          <p className="text-sm text-muted">
            {hasPin ? t("lock.subtitle") : t("lock.subtitleNoPin")}
          </p>
        </div>

        {hasPin ? (
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-foreground">{t("lock.pinLabel")}</span>
              <input
                ref={inputRef}
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="current-password"
                maxLength={6}
                disabled={isLockedOut || submitting}
                className="mt-1 block w-full rounded-md border border-line-strong px-3 py-2.5 text-center text-sm tracking-[0.4em] shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-60"
              />
            </label>

            {error && (
              <p className="text-center text-xs text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isLockedOut || submitting || pin.length < 4}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t("lock.unlock")}
            </button>

            <button
              type="button"
              onClick={handleSignInAgain}
              className="w-full rounded-md border border-line py-2.5 text-sm font-medium text-foreground hover:bg-surface-muted"
            >
              {t("lock.signInAgain")}
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={handleSignInAgain}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90"
          >
            {t("lock.signInAgain")}
          </button>
        )}
      </div>
    </div>
  );
}
